// EN mirror of /industries — industry-specific solutions hub
import { createFileRoute, Link } from "@tanstack/react-router";
import { Briefcase, Building, Stethoscope, GraduationCap, Scale, Plane } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Industry Solutions — Legal, Healthcare, Education, Corporate | NYC Bangkok";
const DESC =
  "Industry-tailored translation & notarisation: law firms (bulk affidavits), hospitals (patient records). Ask us anything — real experts reply within minutes.";

const INDUSTRIES = [
  { slug: "law-firms", icon: Scale, title: "Law Firms", d: "Bulk affidavit notarisation, sworn translation of contracts, certified court exhibits. Retainer pricing for 100+ pages/month." },
  { slug: "healthcare", icon: Stethoscope, title: "Hospitals & Clinics", d: "Patient record translation for medical tourism, sworn translation of vaccination records, HIPAA-aware workflow." },
  { slug: "education", icon: GraduationCap, title: "Education", d: "Transcript & diploma translation, WES/ECE-ready, sworn for visa applications, bulk pricing for institutions." },
  { slug: "corporate", icon: Building, title: "Corporate / M&A", d: "Translation of MoA, AoA, board resolutions, due-diligence packs. Notarisation + apostille for cross-border filings." },
  { slug: "hr-expats", icon: Briefcase, title: "HR / Expat Mobility", d: "Work permit packs, dependent visa documentation, education-credential authentication for relocating employees." },
  { slug: "travel", icon: Plane, title: "Travel & Aviation", d: "Pilot licence translation, crew document apostille, marriage certificate translations for outbound destinations." },
];

export const Route = createFileRoute("/en/industries")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/industries", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/industries", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/industries", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/industries", "en")},
      { property: "og:url", content: `${SITE}/en/industries` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/industries` },
      ...hreflangLinks("/industries", "/en/industries"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: TITLE,
        url: `${SITE}/en/industries`,
        inLanguage: "en",
      }),
    }],
  }),
  component: EnIndustriesHub,
});

function EnIndustriesHub() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Industry-specific <span className="text-amber-300">workflows</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            We serve law firms, hospitals, schools, corporates, and HR teams on retainer. Tailored SLAs, dedicated
            project managers, monthly invoicing.
          </p>
          <div className="quick-answer mt-6 max-w-2xl rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">Does NYC offer corporate retainers for translation and notarisation?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">Yes. NYC Language Institute offers retainer pricing for law firms (100+ pages/month), hospitals, schools, corporates, and HR mobility teams. Dedicated project managers, custom SLAs, monthly net-30 invoicing, and bulk pricing from THB 350/page apply across 22 language pairs.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="px-4 py-14">
        <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {INDUSTRIES.map((i) => (
            <Link key={i.slug} to="/industries/$slug" params={{ slug: i.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:border-amber-400">
              <i.icon className="h-7 w-7 text-amber-600" />
              <h2 className="mt-3 text-base font-bold text-navy-deep group-hover:text-amber-700">{i.title}</h2>
              <p className="mt-2 text-sm text-slate-600">{i.d}</p>
            </Link>
          ))}
        </div>
      </section>
      <section className="border-t border-slate-200 bg-slate-50 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">On-site services across 20+ Thai industrial estates</h2>
          <p className="mt-2 text-sm text-slate-600">Daily courier into EEC, Amata, WHA, Hemaraj, Map Ta Phut, Lat Krabang, Navanakorn and more — notary, ISO 17100 translation, work permit, MFA legalisation.</p>
          <Link to="/en/industrial-estates" className="mt-5 inline-flex items-center gap-2 rounded-xl border border-amber-400 bg-amber-50 px-5 py-3 text-sm font-bold text-amber-700 hover:bg-amber-100">
            Browse all industrial estates →
          </Link>
        <RelatedHubGrid locale="en" />
      </div>
      </section>
    </main>
  );
}
